import { fromStore } from 'svelte/store'

const PRECOGNITION_DEBOUNCE_MS = 1500

// Browser glue for Inertia Precognition: asks the server to validate a field under `scope`
// (`validate('<scope>.<key>')`; laravel-precognition debounces the requests). No rule lives here —
// the server answers with the errors.
//
// When an error may appear ("reward early, punish late"): a text/number/toggle/single-pick field is
// checked once the admin leaves it, so a half-typed value or an untouched blank form shows nothing.
// A list or nested editor (multi-selects, schedule, targeting) is only checked on Save, since
// changing one part of it doesn't mean the rest is finished. A field that already shows an error
// is re-checked on every change so the error clears as soon as it's fixed.

export const changedKeys = (current, previous) =>
  Object.keys(current ?? {}).filter((key) => JSON.stringify(current[key]) !== JSON.stringify(previous?.[key]))

export const isComposite = (value) => typeof value === 'object' && value !== null

export const hasErrorFor = (errors, name) =>
  Object.keys(errors ?? {}).some((key) => key === name || key.startsWith(`${name}.`))

export const isSubmitControl = (target) => Boolean(target?.closest?.('button[type="submit"], input[type="submit"]'))

// Call during component init with a `useForm(...).withPrecognition(...)` store:
// `liveValidate(form, 'place')`. A form that replaces its data in code (a drawer clearing itself
// on open) calls the returned `reset()` right after, so the new values don't count as the admin's
// edits and the next focus change doesn't check the blank fields.
export const liveValidate = (form, scope) => {
  const store = fromStore(form)
  const pending = new Set()
  let previous = null

  const validate = (key) => store.current.validate(`${scope}.${key}`)

  $effect(() => {
    const current = JSON.parse(JSON.stringify(store.current[scope] ?? {}))
    if (previous) {
      for (const key of changedKeys(current, previous)) {
        if (hasErrorFor(store.current.errors, `${scope}.${key}`)) validate(key)
        else if (!isComposite(current[key])) pending.add(key)
      }
    }
    previous = current
  })

  // Pressing Save blurs the field first; a check sent then races the submit and lands after it
  // changed the page. The submit checks every field anyway, so pending checks are dropped, and so
  // is laravel-precognition's debounced call (resetting its timeout cancels the waiting request).
  $effect(() => {
    const flush = () => {
      pending.forEach(validate)
      pending.clear()
    }
    const drop = (event) => {
      if (event.type !== 'submit' && !isSubmitControl(event.target)) return
      pending.clear()
      store.current.setValidationTimeout?.(PRECOGNITION_DEBOUNCE_MS)
    }
    document.addEventListener('pointerdown', drop, true)
    document.addEventListener('submit', drop, true)
    document.addEventListener('focusout', flush)
    return () => {
      document.removeEventListener('pointerdown', drop, true)
      document.removeEventListener('submit', drop, true)
      document.removeEventListener('focusout', flush)
    }
  })

  return {
    reset: () => {
      previous = null
      pending.clear()
    }
  }
}
